import type { ShowroomEmailPreviewSchema } from "api-types/builders/buildersRequests";
import type { BuilderSchemaCopyOne } from "api-types/builders/buildersRequests";
import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import type { StudioDocBaseData } from "api-types/common/responseTypes";
import { WEB_API_BASE_PATH } from "common/constants";
import type { UserNotificationType } from "common/types/userNotification";
import { handleFetchError } from "common/utils/fetchHelpers";
import { getBuilderShowroomEmailPreviewURL } from "lib/endpointHelpers";
import { getWithTimeout, postWithTimeout, putWithTimeout } from "lib/utils/fetchAuthHelpers";
import type { StorageResponse } from "./types";

export interface StartHomePlanDataExportResult {
  jobId: string;
  message: string;
}

export const startHomePlanDataExport = async (
  builderId: string,
): Promise<[StartHomePlanDataExportResult | undefined, UserNotificationType | undefined]> => {
  try {
    const res = await getWithTimeout(`${WEB_API_BASE_PATH}/builders/${builderId}/home-plan-data`);
    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as StartHomePlanDataExportResult, undefined];
  } catch (e) {
    return [undefined, await handleFetchError(e)];
  }
};

export async function putBuilder(
  builderId: string,
  builderFields: Partial<ClientBuilder>,
): StorageResponse<ClientBuilder> {
  try {
    const res = await putWithTimeout(`${WEB_API_BASE_PATH}/builders/${builderId}`, { ...builderFields });

    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as ClientBuilder, undefined];
  } catch (e) {
    return [undefined, await handleFetchError(e)];
  }
}

export async function postBuilder(builder: Partial<ClientBuilder>): StorageResponse<ClientBuilder, null> {
  try {
    const res = await postWithTimeout(`${WEB_API_BASE_PATH}/builders`, { ...builder });

    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as ClientBuilder, null];
  } catch (e) {
    return [null, await handleFetchError(e)];
  }
}

export async function postShowroomEmailPreview(
  builderId: string,
  body: ShowroomEmailPreviewSchema,
): Promise<[string | undefined, UserNotificationType | undefined]> {
  try {
    const res = await postWithTimeout(getBuilderShowroomEmailPreviewURL(builderId), body);
    const json = (await res.json()) as { html: string };

    if (!res.ok) {
      throw json;
    }

    return [json.html, undefined];
  } catch (e) {
    return [undefined, await handleFetchError(e)];
  }
}

export async function getStudioDocsBaseData(builderId: string): StorageResponse<StudioDocBaseData[], null> {
  try {
    const res = await getWithTimeout(`${WEB_API_BASE_PATH}/builders/${builderId}/studio-docs`);
    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as StudioDocBaseData[], null];
  } catch (e) {
    return [null, await handleFetchError(e)];
  }
}

export async function copyBuilderData({
  builderId,
  srcBuilderId,
  fields,
}: {
  builderId: string;
  srcBuilderId: string;
  fields: BuilderSchemaCopyOne;
}): StorageResponse<ClientBuilder, null> {
  try {
    const res = await putWithTimeout(
      `${WEB_API_BASE_PATH}/builders/${builderId}/copy-data?srcBuilderId=${srcBuilderId}`,
      fields,
    );

    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as ClientBuilder, null];
  } catch (e) {
    return [null, await handleFetchError(e)];
  }
}
